import React, { useState, useRef, useEffect } from "react"
import { graphql } from "gatsby"
import styled, { css } from "styled-components"

import Link from "../utils/Link"
import Image from "../utils/Image"

import Box from "../atoms/Box"
import Button from "../atoms/Button"
import Card from "../atoms/Card"
import Flex from "../atoms/Flex"

import SectionTitle from "../molecules/SectionTitle"

const StyledCard = styled(Card)`
  position: relative;
  overflow: hidden;

  /* Render the reveal as an overlay for consistent Safari/Chrome behavior. */
  .card-back-reveal {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 2;
  }

  /* Keep the front logo below the reveal. */
  .card-front-logo {
    position: relative;
    z-index: 1;
  }

  @media (any-hover: hover) {
    &:hover {
      .card-back-reveal {
        opacity: 1;
        pointer-events: auto;
      }
    }
  }

  &.is-revealed {
    .card-back-reveal {
      opacity: 1;
      pointer-events: auto;
    }
  }
`

const StyledRevealCard = styled(StyledCard)`
  position: relative;
  overflow: hidden;

  /* Render the reveal as an overlay for consistent Safari/Chrome behavior. */
  .card-back-reveal {
    position: absolute;
    inset: 0;
    max-width: 100%;
    max-height: 100%;
    pointer-events: none;
    z-index: 2;
  }

  /* Keep the front logo below the reveal. */
  .card-front-logo {
    position: relative;
    z-index: 1;
  }

  @media (any-hover: hover) {
    &:hover {
      .card-back-reveal {
        pointer-events: auto;
      }
    }
  }

  &.is-revealed {
    .card-back-reveal {
      pointer-events: auto;
    }
  }
`

const StyledLink = styled(Link)`
  display: block;
  width: 100%;
  height: 100%;

  @media (any-hover: hover) {
    &:hover {
      .card-front-logo {
        ${props => (props.zoomOnHover ? "transform:scale(1.05)" : null)}
      }
    }
  }
`

const StyledWrapper = styled(Box)`
  height: 100%;

  .logo-card-link,
  .logo-front-link {
    display: block;
    width: 100%;
    height: 100%;
  }

  .card-front-logo {
    transition: transform 0.15s ease-in-out;
  }

  @media (any-hover: hover) {
    &:hover {
      .card-front-logo {
        ${props =>
          props.zoomOnHover &&
          css`
            transform: scale(1.05);
          `}
      }
    }
  }
`

const LogoBox = ({
  image,
  cardFront,
  overlayImage,
  shouldShowOverlay,
  imgMaxHeight,
  imageStyles,
  ...rest
}) => {
  const border = {}
  if (!image) {
    //show empty box if there is not logo
    border.borderColor = "border.0"
    border.borderWidth = "0.5px"
    border.borderStyle = "solid"
  }
  return (
    <Flex
      className="card-front-logo"
      alignItems="center"
      justifyContent="center"
      width="100%"
      height="100%"
      {...rest}
    >
      <Flex
        alignItems="center"
        justifyContent="center"
        position="relative"
        transitionProperty="transform"
        transitionTimingFunction="ease"
        transitionDuration="md"
        minHeight={4}
        width="100%"
        height="100%"
        padding={imageStyles?.padding}
        {...border}
      >
        {image && (
          <Image
            alternativeText="Logo"
            {...image}
            style={{ height: "100%" }}
            imgStyle={{
              objectFit: imageStyles?.objectFit,
              height: "100%",
              width: "100%",
            }}
          />
        )}
      </Flex>
    </Flex>
  )
}

const RevealBox = ({
  align,
  cardBack,
  button,
  sectionTitle,
  verticalAlign,
  shouldShowInfo,
  shouldShowOverlay,
  overlayImage,
  overlayImageStyles,
  width = "100%",
  height = "100%",
  ...rest
}) => {
  return (
    <Card {...cardBack} {...rest}>
      <Box
        position="absolute"
        top={"0px"}
        left={"0px"}
        backgroundColor="inherit"
        width={width}
        height={height}
      >
        {shouldShowInfo && (
          <Flex
            className="card-reveal"
            flexDirection="column"
            justifyContent={
              verticalAlign === "center" ? "center" : "space-between"
            }
            // alignment for button and text content
            alignItems={
              align === "center"
                ? "center"
                : align === "right"
                  ? "flex-end"
                  : "flex-start"
            }
            p={5}
            style={{ zIndex: 100 }}
            height="100%"
            width="100%"
            position="absolute"
          >
            <Box maxHeight="calc(100% - 48px)" overflow="hidden">
              {sectionTitle && <SectionTitle {...sectionTitle} align={align} />}
            </Box>
            {button && (
              <Button
                aria-label="CTA Button in reveal text box for the logo card"
                {...button}
              />
            )}
          </Flex>
        )}
        {shouldShowOverlay && (
          <Flex
            width="100%"
            height="100%"
            style={{
              left: 0,
              bottom: 0,
            }}
            padding={overlayImageStyles?.padding}
          >
            <Image
              className="overlay-image"
              {...overlayImage}
              style={{ height: "100%", width: "100%" }}
              imgStyle={{
                objectFit: overlayImageStyles?.objectFit,
                height: "100%",
                width: "100%",
                opacity: overlayImageStyles?.opacity,
              }}
            />
          </Flex>
        )}
      </Box>
    </Card>
  )
}

const LogoItem = ({
  image,
  overlayImage,
  hoverBehavior,
  cardFront,
  link,
  sectionTitle,
  LSectionTitle,
  button,
  cardBack,
  align,
  verticalAlign,
  zoomOnHover,
  shouldShowInfo = true,
  cardMaxHeight,
  customCardMaxHeight,
  imageStyles,
  overlayImageStyles,
  tags,
  imageSize,
  imageRatio,
  customImageRatio,
  ...rest
}) => {
  const shouldShowOverlay =
    overlayImage && (hoverBehavior === "image" || hoverBehavior === "all")
  sectionTitle = LSectionTitle || sectionTitle
  shouldShowInfo =
    shouldShowInfo && cardBack.useCard && (sectionTitle || button)

  // If card back has info content, use the safe non-link wrapper.
  const useSafeRevealStructure = shouldShowInfo

  const frontCardRef = useRef(null)
  const touchStartRef = useRef({ x: 0, y: 0 })
  const touchMovedRef = useRef(false)
  const blockClickRef = useRef(false)

  const [isRevealed, setIsRevealed] = useState(false)
  
  // const [dimensions, setDimensions] = useState({ width: 0, height: 0 })

  // useEffect(() => {
  //   const element = frontCardRef.current
  //   if (!element) return

  //   const resizeObserver = new ResizeObserver(entries => {
  //     const { width, height } = entries[0].contentRect
  //     setDimensions({ width, height })
  //   })

  //   resizeObserver.observe(element)
  //   return () => resizeObserver.disconnect()
  // }, [])

  // Close the reveal when the user taps/clicks outside the card.
  useEffect(() => {
    if (!isRevealed) return

    const handleOutsidePress = e => {
      if (!frontCardRef.current?.contains(e.target)) {
        setIsRevealed(false)
      }
    }

    document.addEventListener("mousedown", handleOutsidePress)
    document.addEventListener("touchstart", handleOutsidePress)

    return () => {
      document.removeEventListener("mousedown", handleOutsidePress)
      document.removeEventListener("touchstart", handleOutsidePress)
    }
  }, [isRevealed])

  const getImageRatio = imageRatio => {
    switch (imageRatio) {
      case "default":
        return 0.75
      case "taller":
        return 1
      case "threeTwo":
        return 0.66
      case "sixteenNine":
        return 0.5625
      case "custom":
        return customImageRatio / 10
      default:
        return 0.75
    }
  }

  const ratio =
    cardMaxHeight === "fullHeight"
      ? 1
      : cardMaxHeight === "default"
        ? 0.7
        : cardMaxHeight === "custom" && !imageRatio
          ? 1
          : cardMaxHeight === "custom" && imageRatio
            ? getImageRatio(imageRatio)
            : 1

  const maxHeight = !imageRatio
    ? cardMaxHeight === "custom" && customCardMaxHeight > 0
      ? `${customCardMaxHeight}px`
      : "40"
    : null

  const hasTouchRevealContent = shouldShowInfo
  const zoomOnCardHover = hoverBehavior !== "info" ? zoomOnHover : false

  const cardClassName = isRevealed ? "is-revealed" : undefined
  const cardWidth =
    imageSize && !cardFront.useCard ? `calc(${imageSize}%)` : "100%"
  const cardMx =
    imageSize && !cardFront.useCard ? `calc(-${imageSize / 2}%)` : "0"
  const logoWidth =
    imageSize && cardFront.useCard ? `calc(${imageSize}%)` : "100%"
  const logoMx =
    imageSize && cardFront.useCard ? `calc(-${imageSize / 2}%)` : "0"

  const handleTouchStart = e => {
    if (isRevealed || !hasTouchRevealContent) return
    const touch = e.touches?.[0]
    if (!touch) return
    touchStartRef.current = { x: touch.clientX, y: touch.clientY }
    touchMovedRef.current = false
  }

  const handleTouchMove = e => {
    if (isRevealed || !hasTouchRevealContent) return
    const touch = e.touches?.[0]
    if (!touch) return

    const dx = Math.abs(touch.clientX - touchStartRef.current.x)
    const dy = Math.abs(touch.clientY - touchStartRef.current.y)
    if (dx > 10 || dy > 10) touchMovedRef.current = true
  }

  const handleTouchEnd = () => {
    if (isRevealed || !hasTouchRevealContent || touchMovedRef.current) return
    blockClickRef.current = true
    setIsRevealed(true)
  }

  // Block the synthetic click that follows a touch tap so the app does not navigate away.
  const handleClickCapture = e => {
    if (!blockClickRef.current) return
    blockClickRef.current = false
    e.preventDefault()
    e.stopPropagation()
  }

  const logoBox = (
    <LogoBox
      imageStyles={imageStyles}
      image={image}
      cardFront={cardFront}
      overlayImage={overlayImage}
      shouldShowOverlay={shouldShowOverlay}
      width={logoWidth}
      mx={logoMx}
    />
  )

  const revealBox = (shouldShowInfo || shouldShowOverlay) && (
    <RevealBox
      className="card-back-reveal"
      shouldShowInfo={shouldShowInfo}
      shouldShowOverlay={shouldShowOverlay}
      overlayImageStyles={overlayImageStyles}
      overlayImage={overlayImage}
      cardBack={cardBack}
      sectionTitle={sectionTitle}
      align={align}
      verticalAlign={verticalAlign}
      button={button}
      transitionDuration="fast"
      transitionTimingFunction="ease-in-out"
      transitionProperty="opacity"
      opacity={0}
    />
  )

  // Original linked-card structure for image-only/non-interactive cards.
  if (!useSafeRevealStructure) {
    return (
      <StyledLink
        aria-label="Logo link"
        className="logo-card-link"
        zoomOnHover={zoomOnCardHover}
        width="100%"
        {...link}
        optional
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            height: "100%",
          }}
          ref={frontCardRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onClickCapture={handleClickCapture}
        >
          <StyledCard
            {...cardFront}
            {...rest}
            className={cardClassName}
            ratio={ratio}
            maxHeight={maxHeight}
            width={cardWidth}
            mx={cardMx}
          >
            <Flex
              width="100%"
              height="100%"
              justifyContent="center"
              alignItems="center"
            >
              {logoBox}
              {revealBox}
            </Flex>
          </StyledCard>
        </div>
      </StyledLink>
    )
  }

  // Safe non-link wrapper when the reveal has info content.
  return (
    <StyledWrapper
      aria-label="Logo card"
      className="logo-card-link"
      zoomOnHover={zoomOnCardHover}
      width="100%"
    >
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          height: "100%",
        }}
        ref={frontCardRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClickCapture={handleClickCapture}
      >
        <StyledRevealCard
          {...cardFront}
          {...rest}
          className={cardClassName}
          ratio={ratio}
          maxHeight={maxHeight}
          width={cardWidth}
          mx={cardMx}
        >
          <Flex
            width="100%"
            height="100%"
            justifyContent="center"
            alignItems="center"
          >
            <Link
              aria-label="Logo link"
              className="logo-front-link"
              width="100%"
              {...link}
              optional
            >
              {logoBox}
            </Link>
            {revealBox}
          </Flex>
        </StyledRevealCard>
      </div>
    </StyledWrapper>
  )
}

export default LogoItem

export const query = graphql`
  fragment Logo on STRAPI__COMPONENT_MOLECULES_LOGO {
    image {
      ...ImageWithConstrainedLayout
    }
    overlayImage {
      ...ImageWithConstrainedLayout
    }
    LSectionTitle: sectionTitle {
      ...SectionTitle
    }
    button {
      ...Button
    }
    link {
      ...Link
    }
    tags {
      name
    }
    color
    imageSize
  }
`
